import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";
import TableContainer from "components/Common/DataTableContainer";

// API Service for Customer
const customerApiService = {
  // Get customer by ID
  getCustomerById: async (id) => {
    try {
      const response = await fetch(`https://qwikbistro.shop/userProfile/${id}`, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
      });
      return await response.json();
    } catch (error) {
      console.error('Error fetching customer:', error);
      throw error;
    }
  },

  // Update customer
  updateCustomer: async (id, customerData) => {
    try {
      const response = await fetch(`https://qwikbistro.shop/userProfile/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
        body: JSON.stringify(customerData),
      });
      return await response.json();
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  },

  // Get customer orders
  getCustomerOrders: async (userId) => {
    try {
      const response = await fetch(`https://qwikbistro.shop/order/`, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
      });
      const allOrders = await response.json();
      return allOrders.filter(order => order.user_id == userId);
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      throw error;
    }
  },

  // Get customer transactions
  getCustomerTransactions: async (userId) => {
    try {
      const response = await fetch(`https://qwikbistro.shop/transaction/`, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
      });
      const allTransactions = await response.json();
      return allTransactions.filter(transaction => transaction.system_user_id == userId);
    } catch (error) {
      console.error('Error fetching customer transactions:', error);
      throw error;
    }
  },

  // Get menu items
  getMenuItems: async () => {
    try {
      const response = await fetch("https://qwikbistro.shop/menuItem/", {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
      });
      return await response.json();
    } catch (error) {
      console.error('Error fetching menu items:', error);
      throw error;
    }
  },
};

const CustomerDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [activeTab, setActiveTab] = useState("overview");

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch customer data
        const customerData = await customerApiService.getCustomerById(id);
        setCustomer(customerData);
        setEditData(customerData);

        // Fetch customer orders
        const ordersData = await customerApiService.getCustomerOrders(id);
        setOrders(ordersData);

        // Fetch customer transactions
        const transactionsData = await customerApiService.getCustomerTransactions(id);
        setTransactions(transactionsData.reverse());

        // Fetch menu items
        const menuData = await customerApiService.getMenuItems();
        setMenuItems(menuData);

      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load customer data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Calculate statistics
  const totalOrders = orders.length;
  const totalEarnings = orders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);

  const lastOrderDate = orders.length
    ? new Date(orders[orders.length - 1].order_date)
    : null;

  const daysSinceLastOrder = lastOrderDate
    ? Math.max(0, Math.floor((new Date() - new Date(lastOrderDate)) / (1000 * 60 * 60 * 24)))
    : "N/A";

  // Calculate menu item counts
  const menuItemCounts = {};
  orders.forEach(order => {
    order?.orderproductdata?.forEach(item => {
      menuItemCounts[item.name] = (menuItemCounts[item.name] || 0) + (item.quantity || 0);
    });
  });

  // Handlers
  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      const updateData = {
        name: editData?.name,
        email: editData?.email,
        phone: editData?.phone,
        address: editData?.address,
        dob: editData?.dob,
        password_hash: editData?.password_hash,
        qb_user_id: editData?.qb_user_id,
        city: editData?.city,
        note: editData?.note,
      };

      const updatedCustomer = await customerApiService.updateCustomer(id, updateData);
      setCustomer(updatedCustomer);
      setIsEditing(false);
      toast.success("Customer updated successfully");
    } catch (error) {
      console.error("Error updating customer:", error);
      toast.error("Failed to update customer");
    }
  };

  const formatCurrency = (amount) => {
    return `$${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Orders table columns
  const orderColumns = [
    {
      header: "Order Date",
      accessorKey: "order_date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div>
          <div>{formatDate(row.original.order_date)}</div>
          {/* <small className="text-muted">
            {new Date(row.original.order_date).toLocaleDateString('en-US', { weekday: 'short' })}
          </small> */}
        </div>
      ),
    },
    {
      header: "Order ID",
      accessorKey: "qb_order_id",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <button
          className="btn btn-link p-0 text-decoration-none"
          onClick={() => navigate(`/update-qwikbistro-order/${row.original.id}`)}
        >
          {row.original.qb_order_id || "N/A"}
        </button>
      ),
    },
    {
      header: "Items",
      accessorKey: "orderproductdata",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => {
        const items = row.original.orderproductdata || [];
        return (
          <div className="max-w-xs">
            {items.map((item, index) => (
              <div key={index} className="mb-1">
                {index + 1}. {item.name} x{item.quantity}
              </div>
            ))}

          </div>
        );
      },
    },
    {
      header: "Total",
      accessorKey: "total_amount",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => formatCurrency(parseFloat(row.original.total_amount || 0)),
    },
    {
      header: "Status",
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => {
        const status = row.original.status;
        let badgeClass = "bg-secondary";
        let iconClass = "";

        switch (status) {
          case 'completed':
            badgeClass = "bg-success";
            iconClass = "fas fa-check-circle";
            break;
          case 'pending':
            badgeClass = "bg-warning";
            iconClass = "fas fa-hourglass-half";
            break;
          case 'cancelled':
            badgeClass = "bg-danger";
            iconClass = "fas fa-times-circle";
            break;
        }

        return (
          <span className={`badge ${badgeClass}`}>
            <i className={`${iconClass} me-1`}></i>
            {status}
          </span>
        );
      },
    },
  ];

  // Transactions table columns
  const transactionColumns = [
    {
      header: "Date",
      accessorKey: "date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div>
          <div>{formatDate(row.original.date)}</div>
          <small className="text-muted">
            {new Date(row.original.date).toLocaleDateString('en-US', { weekday: 'short' })}
          </small>
        </div>
      ),
    },
    {
      header: "Order ID",
      accessorKey: "qb_order_id",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <button
          className="btn btn-link p-0 text-decoration-none"
          onClick={() => row.original.system_order_id && navigate(`/orders/${row.original.system_order_id}`)}
          disabled={!row.original.system_order_id}
        >
          {row.original.qb_order_id || "-"}
        </button>
      ),
    },
    {
      header: "Total",
      accessorKey: "total",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => formatCurrency(parseFloat(row.original.total || 0)),
    },
    {
      header: "Tips",
      accessorKey: "tips",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => formatCurrency(parseFloat(row.original.tips || 0)),
    },
    {
      header: "Balance",
      accessorKey: "balance",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => formatCurrency(parseFloat(row.original.balance || 0)),
    },
  ];

  if (loading) {
    return <DashboardLoader />;
  }

  if (!customer) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumb title="Customers" breadcrumbItem="Customer Details" />
          <div className="alert alert-danger">Customer not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Customers" breadcrumbItem="Customer Details" />



        {/* Stats Cards */}
        <div className="row mb-4">
          <div className="col-xl-3 col-md-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="font-size-14 mb-0">Total Orders</h5>
                    <div className="mt-3">
                      <h3 className="mb-2">{totalOrders}</h3>
                    </div>

                  </div>
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                      <i className="fas fa-shopping-cart"></i>
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="font-size-14 mb-0">Total Earnings</h5>
                    <div className="mt-3">
                      <h3 className="mb-2">{formatCurrency(totalEarnings)}</h3>
                    </div>

                  </div>
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                      <i className="fas fa-dollar-sign"></i>
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="font-size-14 mb-0">Days Since Last Order</h5>

                    <div className="mt-3">
                      <h3 className="mb-2">{daysSinceLastOrder}</h3>
                    </div>

                  </div>
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                      <i className="fas fa-calendar-day"></i>
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="font-size-14 mb-0">Customer Since</h5>
                    <div className="mt-3">
                      <h3 className="mb-2">{formatDate(customer.created_at)}</h3>
                    </div>

                  </div>
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-info-subtle text-info rounded fs-3">
                      <i className="fas fa-user-plus"></i>
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Header with Stats */}
        <div className="row mb-4">
          {/* <div className="col-md-6">
            <h4 className="page-title mb-0">Customer Details</h4>
            <p className="text-muted">{customer.name} - {customer.qb_user_id}</p>
          </div> */}
          <div className="col-md-12">
            <div className="d-flex justify-content-end gap-2">

              <button
                className="btn btn-warning"
                onClick={() => setIsEditing(!isEditing)}
              >
                <i className={`fas ${isEditing ? 'fa-times' : 'fa-edit'} me-1`}></i>
                {isEditing ? 'Cancel Edit' : 'Edit Customer'}
              </button>
              {isEditing && (
                <button
                  className="btn btn-success"
                  onClick={handleSave}
                >
                  <i className="fas fa-save me-1"></i> Save Changes
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="row">
          <div className="col-lg-4">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-3">Customer Profile</h5>
                <div className="text-center mb-4">
                  {customer.photo ? (
                    <img
                      src={customer.photo}
                      alt="Customer"
                      className="rounded-circle avatar-xl img-thumbnail"
                    />
                  ) : (
                    <div className="rounded-circle avatar-xl img-thumbnail bg-light d-flex align-items-center justify-content-center">
                      <i className="fas fa-user text-muted" style={{ fontSize: '3rem' }}></i>
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Customer ID</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={customer.qb_user_id || "Not specified"}
                    readOnly
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Member Since</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={formatDate(customer.created_at)}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-8">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-3">Customer Information</h5>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Full Name {isEditing && <span className="text-danger">*</span>}</label>
                      {isEditing ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editData.name || ""}
                          onChange={(e) => handleEditChange('name', e.target.value)}
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={customer.name || "N/A"}
                          readOnly
                        />
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Email {isEditing && <span className="text-danger">*</span>}</label>
                      {isEditing ? (
                        <input
                          type="email"
                          className="form-control"
                          value={editData.email || ""}
                          onChange={(e) => handleEditChange('email', e.target.value)}
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={customer.email || "N/A"}
                          readOnly
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Phone {isEditing && <span className="text-danger">*</span>}</label>
                      {isEditing ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editData.phone || ""}
                          onChange={(e) => handleEditChange('phone', e.target.value)}
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={customer.phone || "N/A"}
                          readOnly
                        />
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Date of Birth</label>
                      {isEditing ? (
                        <input
                          type="date"
                          className="form-control"
                          value={editData.dob || ""}
                          onChange={(e) => handleEditChange('dob', e.target.value)}
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={formatDate(customer.dob)}
                          readOnly
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Address</label>
                      {isEditing ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editData.address || ""}
                          onChange={(e) => handleEditChange('address', e.target.value)}
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={customer.address || "N/A"}
                          readOnly
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">City</label>
                      {isEditing ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editData.city || ""}
                          onChange={(e) => handleEditChange('city', e.target.value)}
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={customer.city || "N/A"}
                          readOnly
                        />
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Password</label>
                      {isEditing ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editData.password_hash || ""}
                          onChange={(e) => handleEditChange('password_hash', e.target.value)}
                          placeholder="Leave empty to keep current"
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-control bg-light"
                          value="••••••••"
                          readOnly
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Notes</label>
                      {isEditing ? (
                        <textarea
                          className="form-control"
                          rows="3"
                          value={editData.note || ""}
                          onChange={(e) => handleEditChange('note', e.target.value)}
                        />
                      ) : (
                        <textarea
                          className="form-control bg-light"
                          rows="3"
                          value={customer.note || "No notes"}
                          readOnly
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs for Orders, Transactions, and Menu Items */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* Tabs Navigation */}
                <ul className="nav nav-tabs nav-tabs-custom mb-3" style={{ fontSize: "17px" }}>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                      onClick={() => setActiveTab('overview')}
                    >
                      <i className="fas fa-list-alt me-1"></i> Menu Items Ordered
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'orders' ? 'active' : ''}`}
                      onClick={() => setActiveTab('orders')}
                    >
                      <i className="fas fa-shopping-cart me-1"></i> Orders ({orders.length})
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'transactions' ? 'active' : ''}`}
                      onClick={() => setActiveTab('transactions')}
                    >
                      <i className="fas fa-exchange-alt me-1"></i> Transactions ({transactions.length})
                    </button>
                  </li>
                </ul>

                {/* Tab Content */}
                <div className="tab-content mt-3">
                  {/* Menu Items Ordered Tab */}
                  {activeTab === 'overview' && (
                    <div className="tab-pane fade show active">
                      <h5 className="mb-3">Most Ordered Menu Items</h5>
                      {Object.keys(menuItemCounts).length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th>Menu Item</th>
                                <th className="text-center">Quantity</th>
                                <th className="text-center">Order Count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(menuItemCounts)
                                .sort((a, b) => b[1] - a[1])
                                .map(([name, count]) => {
                                  // Calculate order count
                                  let timesOrdered = 0;
                                  let itemImage = null;

                                  orders.forEach(order => {
                                    const foundItem = order.orderproductdata?.find(
                                      item => item.name === name
                                    );
                                    if (foundItem) {
                                      timesOrdered++;
                                      if (!itemImage) {
                                        itemImage = foundItem.image;
                                      }
                                    }
                                  });

                                  return (
                                    <tr key={name}>
                                      <td>
                                        <div className="d-flex align-items-center">
                                          {itemImage ? (
                                            <img
                                              src={itemImage}
                                              alt={name}
                                              className="avatar-sm rounded me-2"
                                            />
                                          ) : (
                                            <div className="avatar-sm bg-light rounded me-2 d-flex align-items-center justify-content-center">
                                              <i className="fas fa-utensils text-muted"></i>
                                            </div>
                                          )}
                                          <span className="text-truncate" style={{ maxWidth: '200px' }}>
                                            {name}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="text-center">
                                        <span className="badge bg-primary">{count}</span>
                                      </td>
                                      <td className="text-center">
                                        <span className="badge bg-info">{timesOrdered}</span>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="alert alert-info">
                          No menu items ordered yet.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Orders Tab */}
                  {activeTab === 'orders' && (
                    <div className="tab-pane fade show active">
                      <h5 className="mb-3">Customer Orders</h5>
                      {orders.length > 0 ? (
                        <TableContainer
                          columns={orderColumns}
                          data={orders}
                          isGlobalFilter={true}
                          isPagination={true}
                          SearchPlaceholder="Search orders..."
                          pagination="pagination"
                          paginationWrapper="dataTables_paginate paging_simple_numbers"
                          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                          isCustomPageSize={true}
                        />
                      ) : (
                        <div className="alert alert-info">
                          No orders found for this customer.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Transactions Tab */}
                  {activeTab === 'transactions' && (
                    <div className="tab-pane fade show active">
                      <h5 className="mb-3">Customer Transactions</h5>
                      {transactions.length > 0 ? (
                        <TableContainer
                          columns={transactionColumns}
                          data={transactions}
                          isGlobalFilter={true}
                          isPagination={true}
                          SearchPlaceholder="Search transactions..."
                          pagination="pagination"
                          paginationWrapper="dataTables_paginate paging_simple_numbers"
                          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                          isCustomPageSize={true}
                        />
                      ) : (
                        <div className="alert alert-info">
                          No transactions found for this customer.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default CustomerDetailsPage;